import { Alert, Flex, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomerAutoComplete } from "../../../../components/AutoComplete/CustomerAutoComplete.tsx"
import { type CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"
import {
  customerFromSearchResult,
  leadCustomerAddressLine,
  leadCustomerName
} from "./lead-overview-customer.ts"

const { Text } = Typography

interface LeadChangeCustomerModalProps {
  open: boolean
  saving?: boolean
  onCancel: () => void
  onConfirm: (customer: CustomerSearchResult) => Promise<boolean>
}

/** The same customer search the Lead and Project forms use, in a modal. */
function LeadChangeCustomerModal({
  open,
  saving,
  onCancel,
  onConfirm
}: LeadChangeCustomerModalProps) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState<CustomerSearchResult | null>(null)
  const pickedValues = picked ? customerFromSearchResult(picked) : null

  const close = () => {
    setPicked(null)
    onCancel()
  }

  const confirm = async () => {
    if (!picked) return
    if (await onConfirm(picked)) setPicked(null)
  }

  return (
    <Modal
      open={open}
      destroyOnHidden
      maskClosable={false}
      title={t("leadWorkspace.cards.customer.change")}
      okText={t("leadWorkspace.header.save")}
      cancelText={t("leadWorkspace.header.cancel")}
      okButtonProps={{ disabled: !picked }}
      confirmLoading={saving}
      onOk={confirm}
      onCancel={close}
    >
      <Flex vertical gap={"small"}>
        <CustomerAutoComplete
          onSelect={(result: CustomerSearchResult) => setPicked(result)}
          onClear={() => setPicked(null)}
          disabled={saving}
        />

        {pickedValues && (
          <Alert
            type={"info"}
            message={
              leadCustomerName(pickedValues) ??
              t("leadWorkspace.cards.customer.unnamed")
            }
            description={
              <Flex vertical>
                {!!pickedValues.email && <Text>{pickedValues.email}</Text>}
                {!!pickedValues.phone && <Text>{pickedValues.phone}</Text>}
                {!!leadCustomerAddressLine(pickedValues) && (
                  <Text type={"secondary"}>
                    {leadCustomerAddressLine(pickedValues)}
                  </Text>
                )}
              </Flex>
            }
          />
        )}
      </Flex>
    </Modal>
  )
}

export { LeadChangeCustomerModal }
